import API_BASE_URL from '../config/apiConfig';
import { apiClient } from './authService';

/**
 * Fetch paginated notifications for the current user.
 * @param {number} page - The page number (0-indexed).
 * @param {number} size - The number of items per page.
 * @param {'ALL' | 'LANDLORD' | 'BNB_HOST' | 'TENANT'} [side='ALL'] - Filter by notification type for active dashboard.
 * @returns {Promise<Object>} An object containing the notifications array and pagination data.
 */
export const getMyNotifications = async (page = 0, size = 20, side = 'ALL') => {
  try {
    const sideParam = side && side !== 'ALL' ? `&side=${encodeURIComponent(side)}` : '';
    const url = `${API_BASE_URL}/api/notifications/my?page=${page}&size=${size}${sideParam}`;
    const response = await apiClient(url, { method: 'GET' }, true);
    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch notifications';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get my notifications error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Delete a single notification by id.
 * @param {string} notificationId
 * @returns {Promise<Object>}
 */
export const deleteNotification = async (notificationId) => {
  try {
    const id = String(notificationId || '').trim();
    if (!id) {
      throw new Error('Notification ID is required');
    }

    const url = `${API_BASE_URL}/api/notifications/${encodeURIComponent(id)}`;
    const response = await apiClient(url, { method: 'DELETE' }, true);
    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to delete notification';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    if (data.status && data.status !== 'success') {
      const errorMessage = data.message || 'Failed to delete notification';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Delete notification error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Delete all notifications for the current user, optionally filtered by side.
 * @param {'ALL' | 'LANDLORD' | 'BNB_HOST' | 'TENANT'} [side='ALL']
 * @returns {Promise<Object>}
 */
export const clearAllNotifications = async (side = 'ALL') => {
  try {
    const sideParam = side && side !== 'ALL' ? `?side=${encodeURIComponent(side)}` : '';
    const url = `${API_BASE_URL}/api/notifications/my${sideParam}`;
    const response = await apiClient(url, { method: 'DELETE' }, true);
    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to clear notifications';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    if (data.status && data.status !== 'success') {
      const errorMessage = data.message || 'Failed to clear notifications';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Clear all notifications error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};
